import { Children, isValidElement, useMemo, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const text = (c) => Children.toArray(c).map((x) => (typeof x === "string" || typeof x === "number" ? x : "")).join("");

// Drop-in replacement for <select> with <option> children, adds a search field on top of the list.
export function SearchSelect({ value, onChange, children, className = "", disabled, "data-testid": testid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const opts = useMemo(() => Children.toArray(children).flatMap((c) => (Array.isArray(c) ? c : [c]))
    .filter((c) => isValidElement(c) && c.type === "option")
    .map((c) => ({ value: String(c.props.value ?? text(c.props.children)), label: text(c.props.children) })), [children]);
  const cur = opts.find((o) => o.value === String(value ?? "")) || opts[0];
  const words = norm(q).split(/\s+/).filter(Boolean);
  const shown = opts.filter((o) => words.every((w) => norm(o.label).includes(w)));
  const pick = (o) => { onChange({ target: { value: o.value } }); setOpen(false); setQ(""); };
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
      <PopoverTrigger asChild disabled={disabled}>
        <button type="button" data-testid={testid} className={`${className} inline-flex items-center justify-between gap-2 text-left disabled:opacity-60`}>
          <span className={`truncate ${cur && cur.value === "" ? "text-slate-500" : ""}`}>{cur?.label || "—"}</span>
          <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="z-[200] p-0 w-[var(--radix-popover-trigger-width)] min-w-[240px] overflow-hidden rounded-lg border-slate-200 shadow-xl" onOpenAutoFocus={(e) => e.preventDefault()}>
        <div className="flex items-center gap-2 px-3 border-b border-slate-100 bg-slate-50/70">
          <Search className="h-4 w-4 text-slate-400" />
          <input autoFocus data-testid={testid && `${testid}-search`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…"
            onKeyDown={(e) => { if (e.key === "Enter" && shown[0]) { e.preventDefault(); pick(shown[0]); } }}
            className="flex-1 bg-transparent py-2.5 text-sm outline-none placeholder:text-slate-400" />
        </div>
        <div className="max-h-64 overflow-y-auto py-1" data-testid={testid && `${testid}-list`}>
          {shown.length ? shown.map((o) => (
            <button type="button" key={o.value} onClick={() => pick(o)} data-testid={testid && `${testid}-option-${o.value || "none"}`}
              className={`flex w-full items-center gap-2 px-3 py-2 text-sm text-left transition-colors hover:bg-sky-50 ${o.value === cur?.value ? "font-semibold text-sky-800" : "text-slate-700"}`}>
              <Check className={`h-3.5 w-3.5 shrink-0 ${o.value === cur?.value ? "opacity-100" : "opacity-0"}`} /><span className="truncate">{o.label}</span>
            </button>
          )) : <div className="px-3 py-3 text-sm text-slate-500">Aucun résultat</div>}
        </div>
      </PopoverContent>
    </Popover>
  );
}
